import {
  Input,
  Form,
  Switch,
  InputNumber,
  Select,
  Button,
  Divider,
  Popconfirm,
  Image,
  Tooltip,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import {
  ColorHelper,
  ColorProp,
} from '../../../../Models/Devices/ColorOptions';
import {
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
  BarcodeOutlined,
} from '@ant-design/icons';

import style from './EditNotificationSensorBoolean.module.css';

import { DbConnectedSensorBoolean } from '../../../../Models/Devices/DbConnectedSensorBoolean';
import { ImageGallery } from '../../../ImageGallery/ImageGallery';
import { DbImageData } from '../../../../Models/ImageData/DbImageData';
import { HttpHelper } from '../../../../Helpers/HttpHelper';
import CustomSelecter from '../../../CustomSelecter/CustomSelecter';
import { Transput } from '../../../../Models/Platform/Transput';
import { NotificationDevice } from '../../../../Models/Devices/NotificationDevice';
import { DataType } from '../../../../Models/Platform/DataType';

const CustomColorBlock = (props: { color: ColorProp }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'row' }}>
      <div
        style={{
          marginTop: 'auto',
          marginBottom: 'auto',
          marginRight: 10,
          height: 10,
          width: 10,
          borderRadius: 3,
          backgroundColor: props.color.ColorCode,
        }}
      ></div>
      <div>{props.color.ValueName}</div>
    </div>
  );
};

export const EditNotificationSensorBoolean = forwardRef(
  (
    props: {
      allData?: DbConnectedSensorBoolean[];
      data?: DbConnectedSensorBoolean;
      dataDevice?: NotificationDevice;
      dataTransputs: Transput[];
      imageData?: DbImageData[];
      onCancel: Function;
      onRemove: Function;
      onSafe: Function;
    },
    ref,
  ) => {
    const { t, i18n } = useTranslation();
    const [form] = Form.useForm();

    const [connectedSensor, setConnectedSensor] =
      useState<DbConnectedSensorBoolean>();

    const [isModalUploadOpen, setIsModalUploadOpen] = useState(false);

    useImperativeHandle(ref, () => ({
      SafeSensor() {
        if (connectedSensor) {
          form.submit();
          form.validateFields({ validateOnly: true }).then(
            (values: DbConnectedSensorBoolean) => {
              values.id = connectedSensor.id;
              values.imageData = connectedSensor.imageData;
              var newObj: DbConnectedSensorBoolean = Object.assign(
                new DbConnectedSensorBoolean(),
                values,
              );

              props.onSafe(newObj);
            },
            () => {
              props.onSafe(null);
            },
          );
        }
      },
    }));

    useEffect(() => {
      form.resetFields();
      setConnectedSensor(props.data);
    }, [form, props.data, connectedSensor]);

    const onFinishForm = (item: DbConnectedSensorBoolean) => {
      props.onSafe(item);
    };

    const onFinishFormFailed = (errorInfo: any) => {};

    const checkLabelExist = (_: any, value: string) => {
      var foundItem = props.allData?.find((x) => x.moduleName === value);
      if (foundItem !== undefined) {
        if (foundItem.sensorId === props.data?.sensorId) {
          return Promise.resolve();
        }
        return Promise.reject(new Error(t('nameAlreadyExist')));
      }
      return Promise.resolve();
    };

    const messageValidation = () => {
      return t('fillTheField').toString();
    };

    const GetAvailableColors = () => {
      var colors = ColorHelper.GetColors();
      return colors.map((color) => {
        return {
          value: color.KeyName,
          label: <CustomColorBlock color={color}></CustomColorBlock>,
        };
      });
    };

    const onConfirmRemove = () => {
      props.onRemove(connectedSensor);
    };

    const ShowUpload = () => {
      setIsModalUploadOpen(true);
    };

    const ChooseImage = (file: DbImageData) => {
      if (!connectedSensor) {
        return;
      }

      connectedSensor.imageData = file;
      setConnectedSensor(connectedSensor);
      setIsModalUploadOpen(false);
    };

    const SetTransputId = (id: string) => {
      if (!connectedSensor) {
        return;
      }
      form.setFieldValue('sensorId', id);
      connectedSensor.sensorId = id;
      setConnectedSensor(connectedSensor);
    };

    const SetNameOfDevice = () => {
      if (props.dataDevice?.moduleName) {
        return props.dataDevice?.moduleName;
      }
      return '';
    };

    const GetTransputs = () => {
      if (props.dataTransputs == null) {
        return [];
      }
      return props.dataTransputs.filter((x) => x.dataType === DataType.Boolean);
    };

    return (
      <>
        {/* <AddSpecialFunction
          open={isModalOpen}
          sensor={connectedSensor}
          onCancel={() => setIsModalOpen(false)}
          onChoose={ChooseFunction}
        ></AddSpecialFunction> */}

        <ImageGallery
          open={isModalUploadOpen}
          onCancel={() => setIsModalUploadOpen(false)}
          onChooseFuncEnabled={true}
          onChoose={ChooseImage}
        ></ImageGallery>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
          }}
          className={style.devider}
        >
          <Divider
            style={{
              marginRight: 10,
            }}
            // orientation="left"
            // orientationMargin="250"
          >
            {t('edit')}
          </Divider>
          <Button
            style={{ marginTop: 12, marginLeft: 10, width: '50px' }}
            icon={
              <Popconfirm
                title={t('removeSensor')}
                description={t('areYouSure')}
                onConfirm={onConfirmRemove}
                okText={t('yes')}
                cancelText={t('no')}
              >
                <DeleteOutlined style={{ paddingLeft: 6, paddingRight: 6 }} />
              </Popconfirm>
            }
          ></Button>
        </div>

        <Form
          name="basic"
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          style={{ maxWidth: 600 }}
          initialValues={connectedSensor}
          onFinish={onFinishForm}
          onFinishFailed={onFinishFormFailed}
          autoComplete="off"
          form={form}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              width: '100%',
            }}
          >
            <Form.Item
              style={{ width: 540 }}
              label={t('moduleName')}
              name="moduleName"
              key="moduleName"
              rules={[{ required: true, message: t('fillField').toString() }]}
            >
              <Input />
            </Form.Item>
            <Tooltip title={t('copyFromDevice')}>
              <Button
                onClick={() =>
                  form.setFieldValue('moduleName', SetNameOfDevice())
                }
                style={{ marginLeft: 10 }}
                icon={<BarcodeOutlined />}
              ></Button>
            </Tooltip>
          </div>
          <Form.Item
            label={t('sensorName')}
            name="sensorName"
            key="sensorName"
            rules={[{ required: true, message: messageValidation() }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t('sensorId')}
            name="sensorId"
            key="sensorId"
            rules={[
              { required: true, message: messageValidation() },
              { validator: checkLabelExist },
            ]}
          >
            {/* <Input /> */}
            <CustomSelecter
              id={props.data?.sensorId}
              transputs={GetTransputs()}
              setTransputId={SetTransputId}
            ></CustomSelecter>
          </Form.Item>
          <Form.Item
            label={t('visibleEnvironment')}
            name="visible"
            key="visible"
            valuePropName="checked"
            rules={[{ required: true, message: messageValidation() }]}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label={t('showNotification')}
            name="showNotification"
            key="showNotification"
            valuePropName="checked"
            rules={[{ required: true, message: messageValidation() }]}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label={t('switchInputValue')}
            name="switchInputValue"
            key="switchInputValue"
            valuePropName="checked"
            rules={[{ required: true, message: messageValidation() }]}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label={t('image')}
            name="imageData"
            key="imageData"
            rules={[{ required: connectedSensor?.imageData === undefined }]}
          >
            {' '}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              {connectedSensor?.imageData && (
                <Image
                  width={50}
                  src={
                    HttpHelper.GetUrlStorageBaseApi() +
                    connectedSensor?.imageData?.urlLocationOnServer
                  }
                />
              )}
              <Button
                icon={<UploadOutlined />}
                onClick={() => ShowUpload()}
                type="primary"
              >
                {t('selectOtherImage')}
              </Button>
            </div>
          </Form.Item>

          <Form.Item
            label={t('messageByTrue')}
            name="messageByTrue"
            key="messageByTrue"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t('reactionTimeForTrue') + ' (1-10)'}
            name="reactionTimeForTrue"
            key="reactionTimeForTrue"
            rules={[{ required: true }]}
          >
            <InputNumber min={1} max={10} />
          </Form.Item>
          <Form.Item
            label={t('colorForTrue')}
            name="colorForTrue"
            key="colorForTrue"
            rules={[{ required: true }]}
          >
            <Select options={GetAvailableColors()}>
              <Select.Option value="demo">Demo</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            label={t('messageByFalse')}
            name="messageByFalse"
            key="messageByFalse"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t('reactionTimeForFalse') + ' (1-10)'}
            name="reactionTimeForFalse"
            key="reactionTimeForFalse"
            rules={[{ required: true }]}
          >
            <InputNumber min={1} max={10} />
          </Form.Item>
          <Form.Item
            label={t('colorForFalse')}
            key="colorForFalse"
            name="colorForFalse"
            rules={[{ required: true }]}
          >
            <Select options={GetAvailableColors()}>
              <Select.Option value="demo">Demo</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </>
    );
  },
);
